import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Map } from "@mui/icons-material";
import { Button, Tooltip, Typography, useTheme } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import {
  DataGridCard,
  PrefPersistedDataGrid,
  useToolbarHeight,
} from "@pepsico/layout";

import { useGetAllLocations } from "api/locations/locations";
import type { LocationResponse } from "api/model";
import { APP_TITLE, writeMapPrefs } from "defs";

interface LocationsProps {}

const columns: GridColDef<LocationResponse>[] = [
  {
    field: "action",
    headerName: "Actions",
    sortable: false,
    width: 180,
  },
  {
    field: "sector",
    headerName: "Sector",
    valueGetter: (value) => (value != null ? String(value) : ""),
    width: 120,
  },
  {
    field: "region",
    headerName: "Region",
    valueGetter: (value) => (value != null ? String(value) : ""),
    width: 120,
  },
  {
    field: "country",
    headerName: "Country",
    valueGetter: (value) => (value != null ? String(value) : ""),
    width: 120,
  },
  {
    field: "location_name",
    headerName: "Location Name",
    width: 160,
  },
  {
    field: "location_display_name",
    headerName: "Location Display Name",
    width: 160,
  },
  {
    field: "city",
    headerName: "City",
    width: 120,
  },
  {
    field: "state_province",
    headerName: "State / Province",
    width: 140,
  },
  {
    field: "latitude",
    headerName: "Latitude",
    type: "number",
    width: 110,
  },
  {
    field: "longitude",
    headerName: "Longitude",
    type: "number",
    width: 110,
  },
  {
    field: "soil_type",
    headerName: "Soil Type",
    width: 120,
  },
];

const defaultLocations: LocationResponse[] = [];

const Locations: React.FC<LocationsProps> = () => {
  const theme = useTheme();
  const toolbarHeight = useToolbarHeight();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { data: locations = defaultLocations, isPending } = useGetAllLocations(
    undefined,
    {
      query: {
        select: (res): LocationResponse[] =>
          res.status === 200 ? res.data : defaultLocations,
      },
    },
  );

  useEffect(() => {
    const cols = apiRef.current!.getAllColumns();
    const actionColumn = cols.find((col) => col.field === "action");

    if (actionColumn) {
      actionColumn.renderCell = (
        params: GridRenderCellParams<LocationResponse>,
      ) => {
        const hasLatLng =
          params.row.latitude != null && params.row.longitude != null;
        const locationLabel =
          params.row.location_name ?? params.row.location_uuid;

        return (
          <Tooltip disableHoverListener={hasLatLng} title="No lat/lng">
            <span>
              <Button
                aria-label={`View location ${locationLabel} on map`}
                color="primary"
                disabled={!hasLatLng}
                onClick={() => {
                  writeMapPrefs({
                    lat: String(params.row.latitude),
                    lng: String(params.row.longitude),
                    location: params.row.location_uuid || null,
                  });
                  void navigate("/map");
                }}
                size="small"
                startIcon={<Map />}
              >
                View Location
              </Button>
            </span>
          </Tooltip>
        );
      };
    }

    apiRef.current!.updateColumns(cols);
  }, [apiRef, navigate]);

  return (
    <>
      <title>{`${APP_TITLE} - Locations`}</title>
      <Typography gutterBottom sx={{ fontWeight: "bold" }} variant="h6">
        Locations
      </Typography>
      <DataGridCard>
        <PrefPersistedDataGrid
          apiRef={apiRef}
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row: LocationResponse) => row.id}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            sorting: {
              sortModel: [
                { field: "sector", sort: "asc" },
                { field: "region", sort: "asc" },
                { field: "country", sort: "asc" },
                { field: "location_name", sort: "asc" },
              ],
            },
          }}
          loading={isPending}
          pageSizeOptions={[10, 25, 50]}
          pagination
          prefKey="locations-table"
          rows={locations}
          sx={{
            maxHeight: `calc(100vh - ${toolbarHeight}px - ${theme.spacing(10)})`,
          }}
        />
      </DataGridCard>
    </>
  );
};

export default Locations;
